/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useContext } from 'react';
import PropTypes from "prop-types";
import { Droppable } from '@hello-pangea/dnd';
import classNames from 'classnames';
import { useEuiTheme } from '../../services';
import { EuiPanel } from '../panel';
import { EuiDragDropContextContext } from './drag_drop_context';
import { euiDroppableStyles } from './droppable.styles';
export var SPACINGS = ['none', 's', 'm', 'l'];
export var EuiDroppableContext = /*#__PURE__*/React.createContext({
  cloneItems: false
});
export var EuiDroppable = ({
  droppableId,
  direction,
  isDropDisabled = false,
  children,
  className,
  cloneDraggables = false,
  spacing = 'none',
  style,
  type = 'EUI_DEFAULT',
  withPanel = false,
  grow = false,
  'data-test-subj': dataTestSubj = 'droppable',
  ...rest
}) => {
  const {
    isDraggingType
  } = useContext(EuiDragDropContextContext);
  const dropIsDisabled = cloneDraggables ? true : isDropDisabled;
  const euiTheme = useEuiTheme();
  const styles = euiDroppableStyles(euiTheme);
  return <Droppable isDropDisabled={dropIsDisabled} droppableId={droppableId} direction={direction} type={type} {...rest}>
      {(provided, snapshot) => {
      const {
        isDraggingOver
      } = snapshot;
      const PanelOrDiv = withPanel ? EuiPanel : 'div';
      const panelOrDivProps = withPanel ? {
        panelRef: provided.innerRef,
        hasShadow: true,
        paddingSize: 'none'
      } : {
        ref: provided.innerRef
      };
      const cssStyles = [styles.euiDroppable, isDraggingType === type && !dropIsDisabled && styles.isDragging, isDraggingOver && styles.isDraggingOver, grow ? styles.grow : styles.noGrow, styles.spacing[spacing]];
      const classes = classNames('euiDroppable', {
        'euiDroppable-isDisabled': dropIsDisabled
      }, className);
      const DroppableElement = typeof children === 'function' ? children(provided, snapshot) : children;
      return <PanelOrDiv {...provided.droppableProps} {...panelOrDivProps} style={style} data-test-subj={dataTestSubj} className={classes} css={cssStyles}>
            <EuiDroppableContext.Provider value={{
          cloneItems: cloneDraggables
        }}>
              {DroppableElement}
            </EuiDroppableContext.Provider>
            <div className="euiDroppable__placeholder" hidden={cloneDraggables}>
              {provided.placeholder}
            </div>
          </PanelOrDiv>;
    }}
    </Droppable>;
};
EuiDroppable.propTypes = {
  /**
     * ReactNode to render as this component's content
     */
  children: PropTypes.oneOfType([PropTypes.element.isRequired, PropTypes.arrayOf(PropTypes.element.isRequired).isRequired, PropTypes.any.isRequired]).isRequired,
  className: PropTypes.string,
  /**
     * Makes its items immutable. Dragging creates cloned items that can be dropped elsewhere.
     */
  cloneDraggables: PropTypes.bool,
  style: PropTypes.any,
  /**
     * Adds padding to the droppable area
     */
  spacing: PropTypes.any,
  /**
     * Adds an EuiPanel style to the droppable area
     */
  withPanel: PropTypes.bool,
  /**
     * Allow the panel to flex-grow?
     */
  grow: PropTypes.bool,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
try {
  EuiDroppable.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/drag_and_drop/droppable.tsx',
    description: '',
    displayName: 'EuiDroppable',
    methods: [],
    props: {
      children: {
        defaultValue: null,
        description: "ReactNode to render as this component's content",
        name: 'children',
        parent: {
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        }],
        required: true,
        type: {
          name: 'ReactElement | ReactElement<any, string | JSXElementConstructor<any>>[] | ((provided: DroppableProvided, snapshot: DroppableStateSnapshot) => ReactNode)'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      cloneDraggables: {
        defaultValue: {
          value: 'false'
        },
        description: 'Makes its items immutable. Dragging creates cloned items that can be dropped elsewhere.',
        name: 'cloneDraggables',
        parent: {
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      style: {
        defaultValue: null,
        description: '',
        name: 'style',
        parent: {
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      spacing: {
        defaultValue: {
          value: 'none'
        },
        description: 'Adds padding to the droppable area',
        name: 'spacing',
        parent: {
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none" | "m" | "l"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }]
        }
      },
      withPanel: {
        defaultValue: {
          value: 'false'
        },
        description: 'Adds an EuiPanel style to the droppable area',
        name: 'withPanel',
        parent: {
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      grow: {
        defaultValue: {
          value: 'false'
        },
        description: 'Allow the panel to flex-grow?',
        name: 'grow',
        parent: {
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/droppable.tsx',
          name: 'EuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      renderClone: {
        defaultValue: null,
        description: '',
        name: 'renderClone',
        parent: {
          fileName: 'app/node_modules/@hello-pangea/dnd/dist/dnd.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'app/node_modules/@hello-pangea/dnd/dist/dnd.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: 'DraggableChildrenFn'
        }
      },
      droppableId: {
        defaultValue: null,
        description: '',
        name: 'droppableId',
        parent: {
          fileName: 'app/node_modules/@hello-pangea/dnd/dist/dnd.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'app/node_modules/@hello-pangea/dnd/dist/dnd.d.ts',
          name: 'DroppableProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiDroppableProps', 'CommonProps', 'DefaultProps']
  };
} catch (e) {}